/*
 * ByDegree Astrology — theme scripts.
 * Progressive enhancement only: every feature below has a working HTML fallback.
 * No dependencies. Loaded with `defer`.
 */
(function () {
  'use strict';

  var strings = (window.theme && window.theme.strings) || {};
  var desktopQuery = window.matchMedia('(min-width: 990px)');
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var context = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  function getFocusable(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) {
      return el.offsetParent !== null || el.getClientRects().length > 0;
    });
  }

  /* ---------------------------------------------------------------
   * Header: height variable and scrolled state
   * ------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    function setHeight() {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    }
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    if (header.hasAttribute('data-sticky')) {
      var onScroll = function () {
        header.classList.toggle('is-scrolled', window.scrollY > 8);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---------------------------------------------------------------
   * Disclosures: nav dropdowns, header search, filters.
   * Only one open at a time; close on Escape or outside click.
   * ------------------------------------------------------------- */
  function initDisclosures() {
    document.addEventListener('toggle', function (event) {
      var details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;
      if (!details.matches('[data-disclosure], [data-header-search]')) return;

      document.querySelectorAll('[data-disclosure][open], [data-header-search][open]').forEach(function (other) {
        if (other !== details && !other.contains(details)) other.open = false;
      });

      if (details.matches('[data-header-search]')) {
        var input = details.querySelector('input[type="search"]');
        if (input) setTimeout(function () { input.focus(); }, 50);
      }
    }, true);

    document.addEventListener('click', function (event) {
      document.querySelectorAll('[data-disclosure][open], [data-header-search][open]').forEach(function (details) {
        if (!details.contains(event.target)) details.open = false;
      });

      var closer = event.target.closest('[data-close-disclosure]');
      if (closer) {
        var parent = closer.closest('details');
        if (parent) {
          parent.open = false;
          parent.querySelector('summary').focus();
        }
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = event.target.closest && event.target.closest('details[open]');
      if (open && (open.matches('[data-disclosure], [data-header-search]'))) {
        open.open = false;
        open.querySelector('summary').focus();
      }
    });
  }

  /* ---------------------------------------------------------------
   * Desktop mega menus: also open on hover for mouse users
   * ------------------------------------------------------------- */
  function initHoverMenus() {
    var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    document.querySelectorAll('[data-hover-disclosure]').forEach(function (details) {
      if (details.dataset.hoverBound) return;
      details.dataset.hoverBound = 'true';
      var closeTimer;
      details.addEventListener('mouseenter', function () {
        if (!hoverQuery.matches) return;
        clearTimeout(closeTimer);
        if (!details.open) details.dataset.hoverOpened = 'true';
        details.open = true;
      });
      // A mouse click on a menu that hover already opened should not close it again;
      // keyboard activation (event.detail === 0) still toggles normally.
      details.querySelector('summary').addEventListener('click', function (event) {
        if (event.detail > 0 && details.open && details.dataset.hoverOpened) {
          event.preventDefault();
          delete details.dataset.hoverOpened;
        }
      });
      details.addEventListener('mouseleave', function () {
        if (!hoverQuery.matches) return;
        delete details.dataset.hoverOpened;
        closeTimer = setTimeout(function () {
          var active = document.activeElement;
          var keyboardFocusInside = details.contains(active) && active.matches(':focus-visible');
          if (!keyboardFocusInside) details.open = false;
        }, 180);
      });
      details.addEventListener('focusout', function (event) {
        if (event.relatedTarget && !details.contains(event.relatedTarget)) details.open = false;
      });
    });

  }

  /* ---------------------------------------------------------------
   * Mobile menu drawer
   * ------------------------------------------------------------- */
  function initMenuDrawer() {
    var drawer = document.querySelector('[data-menu-drawer]');
    if (!drawer) return;
    var summary = drawer.querySelector('summary');
    var panel = drawer.querySelector('[data-menu-drawer-panel]');

    function setTop() {
      var header = drawer.closest('[data-header]');
      if (header) {
        document.documentElement.style.setProperty('--drawer-top', header.getBoundingClientRect().bottom + 'px');
      }
    }

    drawer.addEventListener('toggle', function () {
      summary.setAttribute('aria-expanded', drawer.open ? 'true' : 'false');
      document.documentElement.classList.toggle('has-drawer-open', drawer.open);
      if (drawer.open) {
        setTop();
        var first = getFocusable(panel)[0];
        if (first) first.focus();
      }
    });
    summary.setAttribute('aria-expanded', 'false');

    drawer.addEventListener('keydown', function (event) {
      if (!drawer.open) return;
      if (event.key === 'Escape') {
        drawer.open = false;
        summary.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = [summary].concat(getFocusable(panel));
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    desktopQuery.addEventListener('change', function (event) {
      if (event.matches && drawer.open) drawer.open = false;
    });
  }

  /* ---------------------------------------------------------------
   * Predictive search
   * ------------------------------------------------------------- */
  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.status = this.querySelector('[data-predictive-status]');
        if (!this.input || !this.results) return;
        this.controller = null;
        this.input.addEventListener('input', debounce(this.onInput.bind(this), 250));
        this.input.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
      }

      onInput() {
        var terms = this.input.value.trim();
        if (terms.length < 2) {
          this.close();
          return;
        }
        this.fetchResults(terms);
      }

      fetchResults(terms) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        var url = this.dataset.url +
          '?q=' + encodeURIComponent(terms) +
          '&section_id=' + this.dataset.section +
          '&resources[type]=product,article,page,collection&resources[limit]=4&resources[options][unavailable_products]=last';
        var self = this;
        fetch(url, { signal: this.controller.signal })
          .then(function (response) {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var content = doc.querySelector('[data-predictive-results-content]');
            if (!content) return;
            self.results.innerHTML = content.outerHTML;
            self.open();
            var count = parseInt(content.getAttribute('data-count'), 10) || 0;
            if (self.status) self.status.textContent = count > 0 ? strings.resultsAvailable : strings.noResults;
          })
          .catch(function (error) {
            if (error.name !== 'AbortError') self.close();
          });
      }

      getOptions() {
        return Array.prototype.slice.call(this.results.querySelectorAll('[role="option"]'));
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          if (!this.results.hidden) {
            event.stopPropagation();
            this.close();
          }
          return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;
        var options = this.getOptions();
        if (!options.length || this.results.hidden) return;
        var current = options.findIndex(function (option) { return option.getAttribute('aria-selected') === 'true'; });

        if (event.key === 'Enter') {
          if (current > -1) {
            event.preventDefault();
            options[current].querySelector('a').click();
          }
          return;
        }

        event.preventDefault();
        var next = event.key === 'ArrowDown' ? current + 1 : current - 1;
        if (next >= options.length) next = 0;
        if (next < 0) next = options.length - 1;
        options.forEach(function (option, index) {
          option.setAttribute('aria-selected', index === next ? 'true' : 'false');
        });
        options[next].id = options[next].id || 'PredictiveOption-' + next;
        this.input.setAttribute('aria-activedescendant', options[next].id);
        options[next].scrollIntoView({ block: 'nearest' });
      }

      onFocusOut() {
        var self = this;
        setTimeout(function () {
          if (!self.contains(document.activeElement)) self.close();
        });
      }

      open() {
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
      }
    });
  }

  /* ---------------------------------------------------------------
   * Quantity input
   * ------------------------------------------------------------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        var self = this;
        this.querySelectorAll('[data-quantity-step]').forEach(function (button) {
          button.addEventListener('click', function () {
            var step = parseInt(button.getAttribute('data-quantity-step'), 10);
            var min = parseInt(self.input.min, 10) || 0;
            var value = (parseInt(self.input.value, 10) || 0) + step;
            self.input.value = Math.max(min, value);
            self.input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }
    });
  }

  /* ---------------------------------------------------------------
   * Cart: submit quantity changes automatically
   * ------------------------------------------------------------- */
  function initCart() {
    var form = document.querySelector('[data-cart-form]');
    if (!form) return;
    var submit = debounce(function () {
      var update = document.createElement('input');
      update.type = 'hidden';
      update.name = 'update';
      update.value = '1';
      form.appendChild(update);
      form.submit();
    }, 600);
    form.addEventListener('change', function (event) {
      if (event.target.name === 'updates[]') submit();
    });
  }

  /* ---------------------------------------------------------------
   * Collection and search filters: apply on change
   * ------------------------------------------------------------- */
  function initFacets() {
    document.querySelectorAll('[data-facets-form]').forEach(function (form) {
      var submit = debounce(function () {
        var params = new URLSearchParams(new FormData(form));
        Array.from(params.keys()).forEach(function (key) {
          params.getAll(key).forEach(function (value) {
            if (value === '') params.delete(key);
          });
        });
        window.location.href = form.getAttribute('action') + '?' + params.toString();
      }, 500);
      form.addEventListener('change', submit);
    });
  }

  /* ---------------------------------------------------------------
   * Product gallery: thumbnails scroll to their slide
   * ------------------------------------------------------------- */
  if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', class extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('[data-gallery-slides]');
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
        if (!this.slider) return;
        var self = this;
        this.thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', function () {
            self.showMedia(thumb.getAttribute('data-gallery-thumb'));
          });
        });
        this.slider.addEventListener('scroll', debounce(this.syncThumbs.bind(this), 80), { passive: true });
        var active = this.querySelector('.gallery__slide.is-active');
        if (active && active !== this.slider.firstElementChild) {
          this.slider.scrollLeft = active.offsetLeft - this.slider.offsetLeft;
        }
      }

      showMedia(mediaId) {
        var slide = this.querySelector('[data-media-id="' + mediaId + '"]');
        if (!slide) return;
        this.slider.scrollTo({
          left: slide.offsetLeft - this.slider.offsetLeft,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
        this.setActive(mediaId);
      }

      syncThumbs() {
        var width = this.slider.clientWidth || 1;
        var index = Math.round(this.slider.scrollLeft / width);
        var slide = this.slider.children[index];
        if (slide) this.setActive(slide.getAttribute('data-media-id'));
      }

      setActive(mediaId) {
        this.querySelectorAll('.gallery__slide').forEach(function (slide) {
          var isTarget = slide.getAttribute('data-media-id') === String(mediaId);
          if (isTarget && !slide.classList.contains('is-active')) {
            slide.classList.add('is-entering');
            setTimeout(function () { slide.classList.remove('is-entering'); }, 500);
          }
          slide.classList.toggle('is-active', isTarget);
        });
        this.thumbs.forEach(function (thumb) {
          if (thumb.getAttribute('data-gallery-thumb') === String(mediaId)) {
            thumb.setAttribute('aria-current', 'true');
          } else {
            thumb.removeAttribute('aria-current');
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------
   * Dialogs (size guide): open with [data-dialog-open="<id>"], close with
   * the X, Escape (native) or a click outside the panel. Focus returns to
   * the button that opened it.
   * ------------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      var dialog = document.getElementById(opener.getAttribute('data-dialog-open'));
      if (!dialog) return;
      event.preventDefault();
      dialog.__opener = opener;
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('has-dialog');
      var close = dialog.querySelector('[data-dialog-close]');
      if (close) close.focus();
      return;
    }

    var closer = event.target.closest('[data-dialog-close]');
    var openDialog = closer ? closer.closest('dialog') : null;
    // A click on the dialog element itself is a click on the backdrop, outside the panel
    if (!openDialog && event.target.matches && event.target.matches('dialog[data-dialog][open]')) {
      openDialog = event.target;
    }
    if (openDialog) {
      if (typeof openDialog.close === 'function') {
        openDialog.close();
      } else {
        openDialog.removeAttribute('open');
        openDialog.dispatchEvent(new Event('close'));
      }
    }
  });

  document.querySelectorAll('dialog[data-dialog]').forEach(function (dialog) {
    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('has-dialog');
      if (dialog.__opener) dialog.__opener.focus();
    });
  });

  /* ---------------------------------------------------------------
   * Variant picker: updates the form, URL, price and buy button
   * using Shopify's Section Rendering API.
   * ------------------------------------------------------------- */
  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', class extends HTMLElement {
      connectedCallback() {
        var data = this.querySelector('[data-variants]');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.section = this.closest('[data-product-section]');
        this.addEventListener('change', this.onChange.bind(this));
        this.updateAvailability();
      }

      getSelected() {
        var selected = [];
        this.querySelectorAll('select[data-option-index]').forEach(function (select) {
          selected[parseInt(select.dataset.optionIndex, 10)] = select.value;
        });
        this.querySelectorAll('input[data-option-index]:checked').forEach(function (input) {
          selected[parseInt(input.dataset.optionIndex, 10)] = input.value;
        });
        return selected;
      }

      onChange(event) {
        var fieldset = event.target.closest('fieldset');
        if (fieldset) {
          var label = fieldset.querySelector('[data-selected-value]');
          if (label) label.textContent = event.target.value;
        }

        var selected = this.getSelected();
        var variant = this.variants.find(function (candidate) {
          return candidate.options.every(function (value, index) {
            return value === selected[index];
          });
        });
        this.updateAvailability();

        var form = document.getElementById(this.dataset.form);
        var button = form && form.querySelector('[data-add-button]');

        if (!variant) {
          if (button) {
            button.disabled = true;
            button.textContent = strings.unavailable;
          }
          return;
        }

        if (form) {
          var idInput = form.querySelector('[data-variant-id]');
          if (idInput) idInput.value = variant.id;
        }

        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        if (variant.featured_media && this.section) {
          var gallery = this.section.querySelector('product-gallery');
          if (gallery && gallery.showMedia) gallery.showMedia(variant.featured_media.id);
        }

        this.renderSection(variant.id);
      }

      updateAvailability() {
        var selected = this.getSelected();
        var variants = this.variants;
        this.querySelectorAll('input[data-option-index]').forEach(function (input) {
          var optionIndex = parseInt(input.dataset.optionIndex, 10);
          var available = variants.some(function (variant) {
            return variant.available && variant.options.every(function (value, index) {
              return index === optionIndex ? value === input.value : value === selected[index];
            });
          });
          input.classList.toggle('is-unavailable', !available);
        });
      }

      renderSection(variantId) {
        if (!this.section) return;
        var sectionId = this.dataset.section;
        var section = this.section;
        fetch(this.dataset.url + '?variant=' + variantId + '&section_id=' + sectionId)
          .then(function (response) { return response.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var scope = section.parentElement || section;
            ['price', 'buy', 'sticky'].forEach(function (key) {
              var source = doc.querySelector('[data-dynamic="' + key + '"]');
              var target = scope.querySelector('[data-dynamic="' + key + '"]');
              if (source && target) target.innerHTML = source.innerHTML;
            });
          })
          .catch(function () {});
      }
    });
  }

  /* ---------------------------------------------------------------
   * Product recommendations
   * ------------------------------------------------------------- */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded || !this.dataset.url) return;
        var self = this;
        var load = function () {
          self.dataset.loaded = 'true';
          fetch(self.dataset.url)
            .then(function (response) { return response.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var fresh = doc.querySelector('product-recommendations');
              if (fresh && fresh.innerHTML.trim().length) self.innerHTML = fresh.innerHTML;
            })
            .catch(function () {});
        };
        if ('IntersectionObserver' in window) {
          var observer = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting) {
              observer.disconnect();
              load();
            }
          }, { rootMargin: '0px 0px 400px 0px' });
          observer.observe(this);
        } else {
          load();
        }
      }
    });
  }

  /* ---------------------------------------------------------------
   * Share button: native share sheet, or copy the link
   * ------------------------------------------------------------- */
  if (!customElements.get('share-button')) {
    customElements.define('share-button', class extends HTMLElement {
      connectedCallback() {
        var button = this.querySelector('[data-share-url]');
        var status = this.querySelector('[data-share-status]');
        if (!button) return;
        button.addEventListener('click', function () {
          var url = button.getAttribute('data-share-url');
          var title = button.getAttribute('data-share-title');
          if (navigator.share) {
            navigator.share({ title: title, url: url }).catch(function () {});
          } else if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(function () {
              if (status) {
                status.textContent = strings.copied;
                setTimeout(function () { status.textContent = ''; }, 3000);
              }
            });
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------
   * Gentle reveal on scroll (disabled for reduced motion)
   * ------------------------------------------------------------- */
  function initReveal(root) {
    (root || document).querySelectorAll('[data-reveal-stagger]').forEach(function (list) {
      Array.prototype.forEach.call(list.children, function (child, index) {
        child.style.setProperty('--reveal-i', index);
      });
    });
    var items = (root || document).querySelectorAll('.reveal-on-scroll:not(.is-revealed)');
    if (!items.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window) || window.Shopify && window.Shopify.designMode) {
      items.forEach(function (item) { item.classList.add('is-revealed'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach(function (item) { observer.observe(item); });
  }


  /* ---------------------------------------------------------------
   * Motion: parallax, orbit tilt and count-up figures.
   * All of it is skipped for reduced motion; the static page is complete without it.
   * ------------------------------------------------------------- */
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var parallaxItems = [];
  var parallaxTicking = false;

  function updateParallax() {
    parallaxTicking = false;
    var viewport = window.innerHeight;
    parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > viewport + 200) return;
      var offset = (rect.top + rect.height / 2 - viewport / 2) * item.factor;
      item.el.style.setProperty('--parallax', offset.toFixed(1) + 'px');
    });
  }

  function requestParallax() {
    if (!parallaxTicking) {
      parallaxTicking = true;
      window.requestAnimationFrame(updateParallax);
    }
  }

  function initParallax(root) {
    if (reducedMotion.matches || window.innerWidth < 750) return;
    (root || document).querySelectorAll('[data-parallax]').forEach(function (el) {
      if (el.hasAttribute('data-parallax-ready')) return;
      el.setAttribute('data-parallax-ready', '');
      parallaxItems.push({ el: el, factor: parseFloat(el.getAttribute('data-parallax')) || 0 });
    });
    if (!initParallax.bound) {
      initParallax.bound = true;
      window.addEventListener('scroll', requestParallax, { passive: true });
      window.addEventListener('resize', debounce(requestParallax, 150));
    }
    requestParallax();
  }

  function initOrbitTilt(root) {
    if (reducedMotion.matches || !finePointer.matches) return;
    (root || document).querySelectorAll('[data-orbit]').forEach(function (orbit) {
      var area = orbit.closest('section, footer, .subjects__stage') || orbit.parentElement;
      if (!area || area.hasAttribute('data-tilt-ready')) return;
      area.setAttribute('data-tilt-ready', '');
      var orbits = area.querySelectorAll('[data-orbit]');
      area.addEventListener('pointermove', function (event) {
        var rect = area.getBoundingClientRect();
        var x = (event.clientX - rect.left) / rect.width - 0.5;
        var y = (event.clientY - rect.top) / rect.height - 0.5;
        orbits.forEach(function (el) {
          el.style.setProperty('--tilt-x', (x * 10).toFixed(2) + 'deg');
          el.style.setProperty('--tilt-y', (y * -10).toFixed(2) + 'deg');
        });
      });
      area.addEventListener('pointerleave', function () {
        orbits.forEach(function (el) {
          el.style.setProperty('--tilt-x', '0deg');
          el.style.setProperty('--tilt-y', '0deg');
        });
      });
    });
  }

  function initCountUp(root) {
    var figures = (root || document).querySelectorAll('[data-count-up]');
    if (!figures.length || reducedMotion.matches || !('IntersectionObserver' in window)) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        var el = entry.target;
        var original = el.textContent.trim();
        var match = original.match(/^(\D*)([\d.,]+)(.*)$/);
        if (!match) return;
        var target = parseFloat(match[2].replace(/,/g, ''));
        if (!isFinite(target)) return;
        var useGrouping = match[2].indexOf(',') !== -1;
        var start = null;
        var duration = 1600;
        el.setAttribute('aria-label', original);
        function frame(time) {
          if (start === null) start = time;
          var progress = Math.min((time - start) / duration, 1);
          var eased = 1 - Math.pow(1 - progress, 4);
          var value = Math.round(target * eased);
          el.textContent = match[1] + (useGrouping ? value.toLocaleString('en-US') : value) + match[3];
          if (progress < 1) {
            window.requestAnimationFrame(frame);
          } else {
            el.textContent = original;
            el.removeAttribute('aria-label');
          }
        }
        window.requestAnimationFrame(frame);
      });
    }, { threshold: 0.4 });
    figures.forEach(function (el) { observer.observe(el); });
  }

  /* ---------------------------------------------------------------
   * Astrology subjects: one subject active at a time; the chart wheel
   * turns to its segment. All descriptions stay in the document.
   * ------------------------------------------------------------- */
  function initSubjects(root) {
    (root || document).querySelectorAll('[data-subjects]').forEach(function (container) {
      if (container.hasAttribute('data-subjects-ready')) return;
      container.setAttribute('data-subjects-ready', '');
      var items = container.querySelectorAll('[data-subject]');
      var labels = container.querySelectorAll('[data-stage-label]');
      var arcs = container.querySelectorAll('[data-arc]');

      function setActive(index) {
        container.style.setProperty('--active', index);
        items.forEach(function (item, i) {
          item.classList.toggle('is-active', i === index);
          var trigger = item.querySelector('[data-subject-trigger]');
          if (trigger) trigger.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        labels.forEach(function (label, i) {
          label.classList.toggle('is-active', i === index);
        });
        arcs.forEach(function (arc) {
          arc.classList.toggle('is-active', parseInt(arc.getAttribute('data-arc'), 10) === index % 6);
        });
      }

      items.forEach(function (item, index) {
        var trigger = item.querySelector('[data-subject-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', function () { setActive(index); });
        trigger.addEventListener('focus', function () { setActive(index); });
        trigger.addEventListener('pointerenter', function () {
          if (desktopQuery.matches && finePointer.matches) setActive(index);
        });
      });
      setActive(0);
    });
  }


  /* ---------------------------------------------------------------
   * Sticky buy bar (product pages): visible once the main purchase area
   * [data-sticky-anchor] has scrolled above the viewport, hidden again
   * while it or any other purchase area [data-sticky-hide] is on screen.
   * ------------------------------------------------------------- */
  function initStickyBuy(root) {
    if (!('IntersectionObserver' in window)) return;
    (root || document).querySelectorAll('[data-sticky-buy]').forEach(function (bar) {
      if (bar.hasAttribute('data-ready')) return;
      var anchor = document.getElementById(bar.getAttribute('data-sticky-buy'));
      if (!anchor) return;
      bar.setAttribute('data-ready', '');
      bar.hidden = false;
      var visible = new Set();
      var update = function () {
        var past = anchor.getBoundingClientRect().bottom < 0;
        bar.classList.toggle('is-visible', past && visible.size === 0);
      };
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        update();
      });
      observer.observe(anchor);
      document.querySelectorAll('[data-sticky-hide]').forEach(function (el) { observer.observe(el); });
      // Fast scrolls can jump past the purchase area without an intersection change
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          update();
        });
      }, { passive: true });
    });
  }

  /* ---------------------------------------------------------------
   * Sale countdown: only rendered for a real end date (see
   * snippets/sale-countdown.liquid). Ticks every second and removes
   * itself when the date passes; it never restarts.
   * ------------------------------------------------------------- */
  function initCountdowns(root) {
    (root || document).querySelectorAll('[data-countdown]').forEach(function (el) {
      if (el.hasAttribute('data-ready')) return;
      el.setAttribute('data-ready', '');
      var end = parseInt(el.getAttribute('data-end'), 10);
      if (!end) return;
      var units = {};
      el.querySelectorAll('[data-unit]').forEach(function (node) { units[node.getAttribute('data-unit')] = node; });
      var pad = function (n) { return (n < 10 ? '0' : '') + n; };
      var timer = null;
      var tick = function () {
        var left = Math.floor((end - Date.now()) / 1000);
        if (left <= 0) {
          if (timer) clearInterval(timer);
          el.remove();
          return;
        }
        if (units.days) units.days.textContent = pad(Math.floor(left / 86400));
        if (units.hours) units.hours.textContent = pad(Math.floor((left % 86400) / 3600));
        if (units.minutes) units.minutes.textContent = pad(Math.floor((left % 3600) / 60));
        if (units.seconds) units.seconds.textContent = pad(left % 60);
      };
      tick();
      if (el.isConnected) timer = setInterval(tick, 1000);
    });
  }

  /* ---------------------------------------------------------------
   * Launch offer (Theme settings › Launch offer). Live: the countdown
   * is ticked by initCountdowns. Preview: countdowns and the popup stay
   * hidden unless the page was opened with ?launch-preview=1 (kept for
   * the visit; ?launch-preview=0 ends it) or in the theme editor;
   * without an end date a preview counts down 14 days from now.
   * ------------------------------------------------------------- */
  var LAUNCH_PREVIEW_KEY = 'bydegree-launch-preview';
  var LAUNCH_POPUP_KEY = 'bydegree-launch-popup-seen';
  var LAUNCH_POPUP_CLOSED_KEY = 'bydegree-popup-closed-at';

  function sessionStore() {
    try {
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function launchPreviewOn() {
    var store = sessionStore();
    var match = /[?&]launch-preview=([01])/.exec(window.location.search);
    if (match && store) {
      try {
        if (match[1] === '1') store.setItem(LAUNCH_PREVIEW_KEY, '1');
        else store.removeItem(LAUNCH_PREVIEW_KEY);
      } catch (error) { /* storage unavailable */ }
    }
    if (match) return match[1] === '1';
    try {
      return !!(store && store.getItem(LAUNCH_PREVIEW_KEY));
    } catch (error) {
      return false;
    }
  }

  function launchVisible(el) {
    if (el.getAttribute('data-launch-mode') === 'live') return true;
    return el.hasAttribute('data-launch-editor') || launchPreviewOn();
  }

  function launchEnd(el) {
    var end = parseInt(el.getAttribute('data-end'), 10);
    if (!end && el.getAttribute('data-launch-mode') === 'preview') {
      end = Date.now() + 14 * 86400000;
      el.setAttribute('data-end', String(end));
    }
    return end || 0;
  }

  function formatLaunchDate(end) {
    try {
      return new Date(end).toLocaleString(undefined, {
        month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
      });
    } catch (error) {
      return new Date(end).toString();
    }
  }

  function initLaunch(root) {
    (root || document).querySelectorAll('[data-launch]').forEach(function (el) {
      if (el.hasAttribute('data-launch-ready')) return;
      el.setAttribute('data-launch-ready', '');
      if (!launchVisible(el)) return;
      var end = launchEnd(el);
      if (!end || end <= Date.now()) {
        el.remove();
        return;
      }
      var date = el.querySelector('[data-launch-date]');
      if (date) {
        date.textContent = formatLaunchDate(end);
        date.setAttribute('datetime', new Date(end).toISOString());
      }
      el.hidden = false;
      el.setAttribute('data-countdown', '');
      initCountdowns(el.parentNode);
    });
  }

  /* Welcome popup: once per visit, after the delay, only while nobody is
   * buying (no product form sent, no cart or checkout link used, nothing
   * in the cart) and no other dialog or text field is in use. */
  function initLaunchPopup() {
    var popup = document.querySelector('[data-launch-popup]');
    if (!popup || popup.hasAttribute('data-ready')) return;
    popup.setAttribute('data-ready', '');
    if (!launchVisible(popup)) return;
    var store = sessionStore();
    try {
      if (store && store.getItem(LAUNCH_POPUP_KEY)) return;
    } catch (error) { /* storage unavailable */ }
    // A closed popup stays away for the set number of days, remembered only where the
    // visitor's privacy choices allow preferences to be stored (Shopify Customer Privacy API).
    var repeatDays = parseInt(popup.getAttribute('data-repeat-days'), 10) || 7;
    var lasting = null;
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed()) {
        lasting = window.localStorage;
      }
    } catch (error) { /* storage unavailable */ }
    try {
      var closedAt = parseInt(window.localStorage.getItem(LAUNCH_POPUP_CLOSED_KEY), 10);
      if (closedAt && Date.now() - closedAt < repeatDays * 86400000) return;
    } catch (error) { /* storage unavailable */ }

    var end = launchEnd(popup);
    var delay = (parseInt(popup.getAttribute('data-delay'), 10) || 18) * 1000;
    var cancelled = false;
    var timer = null;

    var cancel = function () {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    document.addEventListener('submit', function (event) {
      var action = event.target && event.target.getAttribute('action');
      if (action && action.indexOf('/cart') !== -1) cancel();
    }, true);
    document.addEventListener('click', function (event) {
      if (popup.contains(event.target)) return;
      if (event.target.closest && event.target.closest('[data-add-button], button[name="add"], .shopify-payment-button, a[href*="/cart"], a[href*="/checkout"], button[form]')) cancel();
    }, true);

    var close = function () {
      try {
        if (lasting) lasting.setItem(LAUNCH_POPUP_CLOSED_KEY, String(Date.now()));
      } catch (error) { /* storage unavailable */ }
      popup.classList.remove('is-visible');
      document.removeEventListener('keydown', onKey);
      setTimeout(function () { popup.hidden = true; }, 320);
    };
    var onKey = function (event) {
      if (event.key === 'Escape') close();
    };
    popup.querySelectorAll('[data-launch-popup-close], [data-launch-popup-cta]').forEach(function (button) {
      button.addEventListener('click', close);
    });

    var busy = function () {
      if (document.hidden) return true;
      // Only the theme's own dialogs (size guide, menu) pause it; app or consent banners do not.
      if (document.querySelector('dialog[open]')) return true;
      var active = document.activeElement;
      return !!(active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName));
    };
    var attempt = function () {
      if (cancelled) return;
      if (end && end <= Date.now()) return;
      if (busy()) {
        timer = setTimeout(attempt, 4000);
        return;
      }
      try {
        if (store) store.setItem(LAUNCH_POPUP_KEY, '1');
      } catch (error) { /* storage unavailable */ }
      popup.hidden = false;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { popup.classList.add('is-visible'); });
      });
      document.addEventListener('keydown', onKey);
    };
    timer = setTimeout(attempt, delay);
  }

  /* ---------------------------------------------------------------
   * Notices in the bottom corner (snippets/sales-notifications.liquid):
   * one at a time, a limited number per visit, continuing across pages.
   * They wait while the welcome popup or a dialog is open, stop for the
   * visit when closed, and stop once the visitor starts buying. Real
   * purchases older than 14 days are not shown.
   * ------------------------------------------------------------- */
  var NOTIFY_KEY = 'bydegree-notify';

  function initNotify() {
    var box = document.querySelector('[data-notify]');
    if (!box || box.hasAttribute('data-ready')) return;
    box.setAttribute('data-ready', '');
    var store = sessionStore();
    var state = { shown: 0, next: 0, closed: false };
    try {
      state = Object.assign(state, JSON.parse((store && store.getItem(NOTIFY_KEY)) || '{}'));
    } catch (error) { /* storage unavailable */ }
    var save = function () {
      try {
        if (store) store.setItem(NOTIFY_KEY, JSON.stringify(state));
      } catch (error) { /* storage unavailable */ }
    };
    var max = parseInt(box.getAttribute('data-max'), 10) || 6;
    if (state.closed || state.shown >= max) return;

    var relative = null;
    try {
      relative = new Intl.RelativeTimeFormat(document.documentElement.lang || undefined, { numeric: 'auto' });
    } catch (error) { /* older browsers */ }
    var items = Array.prototype.slice.call(box.querySelectorAll('[data-notify-item]')).filter(function (item) {
      var at = item.getAttribute('data-notify-at');
      if (!at) return true;
      var age = Date.now() - Date.parse(at);
      if (!(age >= 0 && age < 14 * 86400000)) {
        item.remove();
        return false;
      }
      var time = item.querySelector('[data-notify-time]');
      if (time) {
        var minutes = Math.round(age / 60000);
        var hours = Math.round(age / 3600000);
        var days = Math.round(age / 86400000);
        if (!relative) time.textContent = new Date(at).toLocaleDateString();
        else if (minutes < 60) time.textContent = relative.format(-Math.max(minutes, 1), 'minute');
        else if (hours < 24) time.textContent = relative.format(-hours, 'hour');
        else time.textContent = relative.format(-days, 'day');
      }
      return true;
    });
    if (!items.length) return;

    var first = (parseInt(box.getAttribute('data-first'), 10) || 20) * 1000;
    var showFor = (parseInt(box.getAttribute('data-show'), 10) || 7) * 1000;
    var gap = (parseInt(box.getAttribute('data-gap'), 10) || 18) * 1000;
    var timer = null;
    var stopped = false;
    var current = null;

    var hide = function () {
      box.classList.remove('is-visible');
      setTimeout(function () {
        if (box.classList.contains('is-visible')) return;
        box.hidden = true;
        if (current) current.hidden = true;
      }, 420);
    };
    var stop = function () {
      stopped = true;
      if (timer) clearTimeout(timer);
      hide();
    };
    var busy = function () {
      if (document.hidden || document.querySelector('dialog[open]')) return true;
      var popup = document.querySelector('[data-launch-popup]');
      return !!(popup && !popup.hidden);
    };
    var showNext = function () {
      if (stopped) return;
      if (busy()) {
        timer = setTimeout(showNext, 4000);
        return;
      }
      if (state.shown >= max) return;
      if (current) current.hidden = true;
      current = items[state.next % items.length];
      current.hidden = false;
      state.next = (state.next + 1) % items.length;
      state.shown += 1;
      save();
      box.hidden = false;
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { box.classList.add('is-visible'); });
      });
      timer = setTimeout(function () {
        hide();
        if (state.shown < max) timer = setTimeout(showNext, gap);
      }, showFor);
    };

    box.querySelector('[data-notify-close]').addEventListener('click', function () {
      state.closed = true;
      save();
      stop();
    });
    // Pause while the visitor points at or focuses a notice
    box.addEventListener('mouseenter', function () { if (timer) clearTimeout(timer); });
    box.addEventListener('mouseleave', function () {
      if (stopped || !box.classList.contains('is-visible')) return;
      timer = setTimeout(function () {
        hide();
        if (state.shown < max) timer = setTimeout(showNext, gap);
      }, 2500);
    });
    document.addEventListener('submit', function (event) {
      var action = event.target && event.target.getAttribute('action');
      if (action && action.indexOf('/cart') !== -1) stop();
    }, true);
    document.addEventListener('click', function (event) {
      if (box.contains(event.target)) return;
      if (event.target.closest && event.target.closest('[data-add-button], button[name="add"], .shopify-payment-button, a[href*="/checkout"], button[form]')) stop();
    }, true);

    timer = setTimeout(showNext, state.shown > 0 ? gap / 2 : first);
  }

  /* ---------------------------------------------------------------
   * Announcement ribbon (marquee): keeps enough identical copies of the
   * message list side by side to always cover the bar, and moves the lane
   * by exactly one copy per cycle (CSS animation), so the loop is seamless.
   * One cycle takes 25-35 s depending on the real width of the messages.
   * Copies are aria-hidden and inert; only the first list is announced.
   * ------------------------------------------------------------- */
  function initMarquee(root) {
    (root || document).querySelectorAll('[data-marquee]').forEach(function (box) {
      if (box.hasAttribute('data-ready')) return;
      box.setAttribute('data-ready', '');
      var lane = box.querySelector('[data-marquee-lane]');
      var group = lane && lane.querySelector('[data-marquee-group]');
      var template = lane && lane.querySelector('[data-marquee-copy]');
      if (!group || !template) return;
      var lastWidth = 0;

      var layout = function () {
        var groupWidth = group.getBoundingClientRect().width;
        var boxWidth = box.getBoundingClientRect().width;
        if (!groupWidth || !boxWidth) return;
        // The group plus its copies must stay wider than the bar while the lane moves one group left
        var needed = Math.max(2, Math.ceil(boxWidth / groupWidth) + 1);
        var copies = lane.querySelectorAll('[data-marquee-copy]');
        for (var i = copies.length + 1; i < needed; i++) lane.appendChild(template.cloneNode(true));
        for (var j = copies.length; j >= needed; j--) {
          if (copies[j - 1] && copies[j - 1] !== template) copies[j - 1].remove();
        }
        if (Math.abs(groupWidth - lastWidth) < 0.5) return;
        lastWidth = groupWidth;
        lane.style.setProperty('--marquee-distance', (-groupWidth).toFixed(2) + 'px');
        lane.style.setProperty('--marquee-duration', Math.min(35, Math.max(25, groupWidth / 38)).toFixed(2) + 's');
      };

      // Keyboard focus may scroll the clipped ribbon to show a link; put it back afterwards
      box.addEventListener('focusout', function () {
        setTimeout(function () {
          if (!box.contains(document.activeElement)) box.scrollLeft = 0;
        }, 0);
      });

      layout();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
      if ('ResizeObserver' in window) {
        new ResizeObserver(layout).observe(box);
      } else {
        window.addEventListener('resize', layout);
      }
    });
  }

  /* ---------------------------------------------------------------
   * Announcement bar: rotates messages; pauses on hover, focus and
   * hidden tabs. Inactive messages are inert so links stay reachable
   * only while visible.
   * ------------------------------------------------------------- */
  function initAnnouncements(root) {
    (root || document).querySelectorAll('[data-announcement]').forEach(function (track) {
      if (track.hasAttribute('data-ready')) return;
      track.setAttribute('data-ready', '');
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-announcement-slide]'));
      if (slides.length < 2) return;
      var interval = parseInt(track.getAttribute('data-interval'), 10) || 4500;
      var index = 0;
      var timer = null;
      var paused = false;

      function show(next) {
        if (next === index) return;
        var current = slides[index];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        current.setAttribute('inert', '');
        setTimeout(function () { current.classList.remove('is-leaving'); }, 1100);
        index = (next + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        slides[index].removeAttribute('inert');
      }

      function start() {
        stop();
        if (!paused && !document.hidden) timer = setInterval(function () { show(index + 1); }, interval);
      }

      function stop() {
        if (timer) clearInterval(timer);
        timer = null;
      }

      track.addEventListener('mouseenter', function () { paused = true; stop(); });
      track.addEventListener('mouseleave', function () { paused = false; start(); });
      track.addEventListener('focusin', function () { paused = true; stop(); });
      track.addEventListener('focusout', function () { paused = false; start(); });
      document.addEventListener('visibilitychange', start);
      document.addEventListener('shopify:block:select', function (event) {
        var i = slides.indexOf(event.target);
        if (i === -1) return;
        paused = true;
        stop();
        show(i);
      });
      document.addEventListener('shopify:block:deselect', function () { paused = false; start(); });
      start();
    });
  }


  /* ---------------------------------------------------------------
   * Add to cart feedback: the item is added immediately (AJAX), the
   * buttons show “Added to cart” with a check, then the visitor lands
   * on the cart page as before. If the request fails, the form is
   * submitted normally so Shopify can show its own message.
   * ------------------------------------------------------------- */
  function initCartFeedback(root) {
    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add';
    var cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    (root || document).querySelectorAll('form[data-cart-feedback]').forEach(function (form) {
      if (form.hasAttribute('data-ready') || !window.fetch || !window.FormData) return;
      form.setAttribute('data-ready', '');
      form.addEventListener('submit', function (event) {
        if (form.hasAttribute('data-submitting-native')) return;
        event.preventDefault();
        var buttons = Array.prototype.slice.call(form.querySelectorAll('[data-add-button]'));
        // getAttribute: form.id would return the hidden input named "id"
        var formId = form.getAttribute('id');
        if (formId) {
          buttons = buttons.concat(Array.prototype.slice.call(document.querySelectorAll('button[form="' + formId + '"]')));
        }
        var template = form.parentElement && form.parentElement.querySelector('template[data-added-label]');
        buttons.forEach(function (button) {
          button.setAttribute('aria-busy', 'true');
          button.classList.add('is-loading');
        });
        var native = function () {
          form.setAttribute('data-submitting-native', '');
          HTMLFormElement.prototype.submit.call(form);
        };
        var errorBox = form.hasAttribute('data-cart-inline-errors') && form.parentElement
          ? form.parentElement.querySelector('[data-cart-error]')
          : null;
        if (errorBox) {
          errorBox.hidden = true;
          errorBox.textContent = '';
        }
        fetch(addUrl + '.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
          .then(function (response) {
            // Forms with an inline error area show Shopify's message (e.g. sold out) in place
            if (!response.ok && errorBox && response.status >= 400 && response.status < 500) {
              return response.json().catch(function () { return {}; }).then(function (data) {
                buttons.forEach(function (button) {
                  button.classList.remove('is-loading');
                  button.removeAttribute('aria-busy');
                });
                errorBox.textContent = (data && (data.description || data.message)) || errorBox.getAttribute('data-fallback') || '';
                errorBox.hidden = false;
                var stop = new Error('handled');
                stop.handled = true;
                throw stop;
              });
            }
            if (!response.ok) throw new Error('add failed');
            buttons.forEach(function (button) {
              button.classList.remove('is-loading');
              button.classList.add('is-added');
              if (template) button.innerHTML = template.innerHTML;
            });
            var live = form.parentElement && form.parentElement.querySelector('[data-cart-status]');
            if (live && template) live.textContent = template.content.textContent.trim();
            setTimeout(function () { window.location.href = cartUrl; }, 650);
          })
          .catch(function (error) {
            if (error && error.handled) return;
            native();
          });
      });
    });
  }

  /* ---------------------------------------------------------------
   * One-time entrance for [data-animate-in] (e.g. the price block)
   * when it first enters the viewport.
   * ------------------------------------------------------------- */
  function initAnimateIn(root) {
    var items = (root || document).querySelectorAll('[data-animate-in]:not(.is-in)');
    if (!items.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (item) { item.classList.add('is-in'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    items.forEach(function (item) { observer.observe(item); });
  }

  /* ---------------------------------------------------------------
   * Customer accounts: login/recover toggle, address helpers
   * ------------------------------------------------------------- */
  function initCustomerLogin() {
    var login = document.getElementById('CustomerLogin');
    var recover = document.getElementById('RecoverPassword');
    if (!login || !recover) return;

    function show(panelId) {
      login.hidden = panelId !== 'CustomerLogin';
      recover.hidden = panelId !== 'RecoverPassword';
      var heading = document.querySelector('#' + panelId + ' h1');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    document.querySelectorAll('[data-customer-toggle]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        show(link.getAttribute('data-customer-toggle'));
      });
    });

    if (window.location.hash === '#recover' || recover.querySelector('[role="status"], .errors')) {
      login.hidden = true;
      recover.hidden = false;
    }
  }

  function initCustomerAddresses() {
    document.querySelectorAll('form[data-confirm-message]').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        if (!window.confirm(form.getAttribute('data-confirm-message'))) event.preventDefault();
      });
    });

    if (!window.Shopify || typeof window.Shopify.CountryProvinceSelector !== 'function') return;
    document.querySelectorAll('select[id^="AddressCountry_"]').forEach(function (countrySelect) {
      var provinceId = 'AddressProvince_' + countrySelect.id.replace('AddressCountry_', '');
      if (document.getElementById(provinceId)) {
        new window.Shopify.CountryProvinceSelector(countrySelect.id, provinceId);
      }
    });
  }

  /* ---------------------------------------------------------------
   * Init
   * ------------------------------------------------------------- */
  function init() {
    initHeader();
    initDisclosures();
    initHoverMenus();
    initMenuDrawer();
    initCart();
    initFacets();
    initReveal();
    initParallax();
    initOrbitTilt();
    initCountUp();
    initSubjects();
    initStickyBuy();
    initCountdowns();
    initLaunch();
    initLaunchPopup();
    initNotify();
    initMarquee();
    initAnnouncements();
    initCartFeedback();
    initAnimateIn();
    initCustomerLogin();
    initCustomerAddresses();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run per-section behaviour when a section is re-rendered
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
    initParallax(event.target);
    initOrbitTilt(event.target);
    initCountUp(event.target);
    initSubjects(event.target);
    initStickyBuy(event.target);
    initCountdowns(event.target);
    initLaunch(event.target);
    initMarquee(event.target);
    initAnnouncements(event.target);
    initCartFeedback(event.target);
    initAnimateIn(event.target);
    if (event.target.querySelector('[data-header]')) {
      initHeader();
      initHoverMenus();
      initMenuDrawer();
    }
    if (event.target.querySelector('[data-facets-form]')) initFacets();
  });
})();
